import {
  ArrowsRightLeftIcon,
  ComputerDesktopIcon,
  CreditCardIcon,
  QuestionMarkCircleIcon,
  TrophyIcon,
  UsersIcon,
} from '@heroicons/react/24/outline';
import { UserCircleIcon } from '@heroicons/react/24/solid';
import { Menu, MenuItem, Divider } from '@mui/material';
import { inject, observer } from 'mobx-react';
import { useNavigate } from 'react-router-dom';
import { useHeaderSettingsStore } from './store';
import useThemeStore from 'Theme/store';
import ToggleTheme from 'Components/Common/ToggleTheme';
import useChatStore from 'Components/Chat/store';

const UserSettingsMenu = ({ store, anchorEl, open, handleClose }) => {
  const navigate = useNavigate();
  const { theme: themeValue } = useThemeStore();
  const setPowerPanelOpen = useChatStore((state) => state.setPowerPanelOpen);
  const { setShowTeamsModal, setOpenDialogDiscord } = useHeaderSettingsStore();
  const handleNavigation = (path) => {
    navigate(path);
    handleClose();
    setPowerPanelOpen(false);
  };

  const handleUserGuideOrModal = () => {
    const LTDs = [
      'straico_tier1',
      'straico_tier2',
      'straico_tier3',
      'straico_tier4',
      'straico_tier5',
    ];

    if (LTDs.includes(store.profile.plan)) {
      window.userGuiding.previewGuide(140145);
    } else {
      setShowTeamsModal(true);
    }
    handleClose();
  };

  const handleDiscord = () => {
    setOpenDialogDiscord(true);
    handleClose();
  };

  return (
    <Menu
      id="user-settings-button"
      open={open}
      anchorEl={anchorEl}
      onClose={handleClose}
      slotProps={{
        list: {
          'aria-labelledby': 'user-settings-button',
          className: 'p-4 rounded-xl min-w-[270px] max-w-[320px] bg-inherit',
        },
        paper: {
          sx: {
            borderRadius: '12px',
            overflow: 'visible',
            filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
            p: 2,
            ml: 2,
            mt: 1.5,
            '& .MuiMenuItem-root': {
              mt: 0.5,
              padding: '4px 12px',
              borderRadius: '12px',
            },
          },
        },
      }}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'center',
      }}
    >
      <div className="flex items-center gap-1 px-2 pb-2 pt-1 mb-2 border-b-0">
        <button
          onClick={handleDiscord}
          className={`flex items-center justify-center px-1 rounded-md text-sm ${
            themeValue === 'dark'
              ? 'hover:bg-ship-grey text-crystal-bell'
              : 'hover:bg-lavender text-cool-gray'
          }`}
        >
          <img
            className="size-6 px-1 block"
            src={
              themeValue === 'dark'
                ? '/svg/discord-mark-white.svg'
                : '/svg/discord-mark-blue.svg'
            }
            alt="discord"
          />
        </button>
        <ToggleTheme />
        <a
          href="https://straico.com/multimodel/"
          target="_blank"
          rel="noreferrer"
          className="flex"
          onClick={handleClose}
        >
          <button
            className={`flex items-center justify-center px-1 rounded-md ${
              themeValue === 'dark'
                ? 'text-crystal-bell hover:bg-ship-grey'
                : 'text-raisin-black hover:bg-lavender'
            } text-sm`}
          >
            <QuestionMarkCircleIcon className="w-5 h-5 mx-1" />
          </button>
        </a>
      </div>

      <Divider className="bg-cool-gray mb-1" />

      <MenuItem
        onClick={() => handleNavigation('/my-profile')}
        className={`flex items-center gap-3 py-2.5 px-2 rounded-lg ${
          themeValue === 'dark'
            ? 'text-crystal-bell hover:bg-ship-grey hover:text-tropical-indigo'
            : 'text-raisin-black hover:bg-lavender hover:text-violet-blue'
        }`}
      >
        {store?.profile?.avatar?.svg ? (
          <div className="avatar">
            <div className="w-6 rounded">
              <div
                dangerouslySetInnerHTML={{
                  __html: store.profile.avatar.svg,
                }}
              />
            </div>
          </div>
        ) : (
          <UserCircleIcon className="w-5 h-5" />
        )}
        <span className="ml-1">User settings</span>
      </MenuItem>

      <MenuItem
        onClick={() => handleNavigation('/transactions')}
        className={`flex items-center gap-3 py-2.5 px-2 rounded-lg ${
          themeValue === 'dark'
            ? 'text-crystal-bell hover:bg-ship-grey hover:text-tropical-indigo'
            : 'text-raisin-black hover:bg-lavender hover:text-violet-blue'
        }`}
      >
        <ArrowsRightLeftIcon className="w-5 h-5" />
        <span className="ml-1">Transactions</span>
      </MenuItem>

      <MenuItem
        onClick={() => handleNavigation('/my-profile/pricing')}
        className={`flex items-center gap-3 py-2.5 px-2 rounded-lg ${
          themeValue === 'dark'
            ? 'text-crystal-bell hover:bg-ship-grey hover:text-tropical-indigo'
            : 'text-raisin-black hover:bg-lavender hover:text-violet-blue'
        }`}
      >
        <CreditCardIcon className="w-5 h-5" />
        <span className="ml-1">Billing and plans</span>
      </MenuItem>

      <MenuItem
        onClick={() => handleNavigation('/rewards')}
        className={`flex items-center gap-3 py-2.5 px-2 rounded-lg ${
          themeValue === 'dark'
            ? 'text-crystal-bell hover:bg-ship-grey hover:text-tropical-indigo'
            : 'text-raisin-black hover:bg-lavender hover:text-violet-blue'
        }`}
      >
        <TrophyIcon className="w-5 h-5" />
        <span className="ml-1">Rewards</span>
      </MenuItem>

      <MenuItem
        onClick={handleUserGuideOrModal}
        className={`flex items-center gap-3 py-2.5 px-2 rounded-lg ${
          themeValue === 'dark'
            ? 'text-crystal-bell hover:bg-ship-grey hover:text-tropical-indigo'
            : 'text-raisin-black hover:bg-lavender hover:text-violet-blue'
        }`}
      >
        <UsersIcon className="w-5 h-5" />
        <span className="ml-1">Teams</span>
      </MenuItem>

      <MenuItem
        component="a"
        href="https://straico.com/desktop-app/"
        target="_blank"
        rel="noreferrer"
        onClick={handleClose}
        className={`flex items-center gap-3 py-2.5 px-2 rounded-lg ${
          themeValue === 'dark'
            ? 'text-crystal-bell hover:bg-ship-grey hover:text-tropical-indigo'
            : 'text-raisin-black hover:bg-lavender hover:text-violet-blue'
        }`}
      >
        <ComputerDesktopIcon className="w-5 h-5" />
        <div className="ml-1 flex flex-col leading-tight">
          <span>Desktop app</span>
          <span className="text-[10px] mt-[-2px]">Beta</span>
        </div>
      </MenuItem>
    </Menu>
  );
};

export default inject('store')(observer(UserSettingsMenu));
